import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X, UserPlus, UserMinus, Pencil, Trash2 } from 'lucide-react';
import { useLicense, useAtribuirSeat, useRevogarSeat } from '../hooks';
import { CustodianAutocomplete } from './CustodianAutocomplete';
import { formatarData, formatarMoeda } from '../utils/formatters';
import type { Custodian } from '../api';

interface Props {
  open: boolean;
  licenseId: string;
  canWrite: boolean;
  onClose: () => void;
  /** Editar e excluir ficam no detalhe desde que a aba Licenças saiu (issue #464). */
  onEdit?: () => void;
  onDelete?: () => void;
}

export function LicencaDetalheModal({ open, licenseId, canWrite, onClose, onEdit, onDelete }: Props) {
  const query = useLicense(open ? licenseId : undefined);
  const atribuir = useAtribuirSeat(licenseId);
  const revogar = useRevogarSeat(licenseId);
  const [custodian, setCustodian] = useState<Custodian | null>(null);
  const [error, setError] = useState<string | null>(null);

  const d = query.data;

  async function handleAtribuir() {
    setError(null);
    if (!custodian) return;
    try {
      await atribuir.mutateAsync({ custodianId: custodian.id });
      setCustodian(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao atribuir seat'));
    }
  }

  async function handleRevogar(seatId: string) {
    setError(null);
    try {
      await revogar.mutateAsync(seatId);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao revogar seat'));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {d ? `${d.license.name} — ${d.license.vendor}` : 'Detalhe da licença'}
            </Dialog.Title>
            <div className="flex items-center gap-1">
              {d && onEdit && (
                <button
                  onClick={onEdit}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
              )}
              {d && onDelete && (
                <button
                  onClick={onDelete}
                  className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Excluir
                </button>
              )}
              <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Fechar">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {query.isLoading && <p className="py-6 text-center text-sm text-gray-500">Carregando...</p>}

          {d && (
            <>
              <div className="mb-4 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <p><span className="text-gray-500">Tipo:</span> <span className="text-gray-900">{d.license.licenseType}</span></p>
                <p><span className="text-gray-500">Contratada em:</span> <span className="text-gray-900">{formatarData(d.license.contractedAt)}</span></p>
                <p><span className="text-gray-500">Vencimento:</span> <span className="text-gray-900">{d.license.expiresAt ? formatarData(d.license.expiresAt) : 'Perpétua'}</span></p>
                <p><span className="text-gray-500">Custo por seat:</span> <span className="text-gray-900">{d.license.costPerSeat ? formatarMoeda(d.license.costPerSeat) : '—'}</span></p>
                <p><span className="text-gray-500">Total:</span> <span className="text-gray-900">{d.license.totalCost ? formatarMoeda(d.license.totalCost) : '—'}</span></p>
                <p>
                  <span className="text-gray-500">Uso:</span>
                  <span className="ml-2 text-gray-900">
                    <strong>{d.license.usedSeats}</strong> / {d.license.totalSeats}
                    <span className={`ml-2 text-xs ${d.license.availableSeats <= 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      ({d.license.availableSeats > 0 ? `${d.license.availableSeats} disponível(is)` : 'sem saldo'})
                    </span>
                  </span>
                </p>
              </div>

              {canWrite && (
                <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
                  <p className="mb-2 text-xs font-medium text-blue-900">Atribuir seat a um responsável</p>
                  <CustodianAutocomplete value={custodian} onChange={setCustodian} />
                  <button
                    onClick={handleAtribuir}
                    disabled={!custodian || atribuir.isPending || d.license.availableSeats <= 0}
                    title={d.license.availableSeats <= 0 ? 'Limite de licenças contratadas atingido (RN006)' : ''}
                    className="mt-2 inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <UserPlus className="h-3 w-3" />
                    {atribuir.isPending ? 'Atribuindo...' : 'Atribuir seat'}
                  </button>
                  {d.license.availableSeats <= 0 && (
                    <p className="mt-1 text-[11px] text-red-700">Limite de licenças contratadas atingido — revogue seats antes.</p>
                  )}
                </div>
              )}

              {error && (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>
              )}

              <div>
                <h4 className="mb-2 text-sm font-semibold text-gray-700">Seats atribuídos</h4>
                {d.seats.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhum seat atribuído.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Responsável</th>
                        <th className="px-2 py-1.5 text-left">Desde</th>
                        <th className="px-2 py-1.5 text-left">Status</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {d.seats.map((s) => (
                        <tr key={s.id}>
                          <td className="px-2 py-1.5 text-gray-800">{s.custodianName ?? '—'}</td>
                          <td className="px-2 py-1.5 text-gray-700">{formatarData(s.assignedAt)}</td>
                          <td className="px-2 py-1.5">
                            {s.revokedAt ? (
                              <span className="text-xs text-gray-500">Revogado em {formatarData(s.revokedAt)}</span>
                            ) : (
                              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">Ativo</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            {canWrite && !s.revokedAt && (
                              <button
                                onClick={() => handleRevogar(s.id)}
                                className="inline-flex items-center gap-1 rounded border border-red-300 bg-white px-2 py-0.5 text-xs text-red-700 hover:bg-red-50"
                              >
                                <UserMinus className="h-3 w-3" /> Revogar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
